import React, { useContext, useEffect, useState } from "react";
import { Table, Tabs, Input, Spin, Card } from "antd";
import Breadcrumbs from "../Component/Breadcrumbs";
import constants from "../../Constants/constants";
import { useNavigate, useOutletContext } from "react-router-dom";
import { fetchStaffApi } from "../../Services/Api";
import { breadcrumbHelper, checkWhitespaces, getNextAndPreviousButtons, HighlightedText, performSearch, setContextOnLoad, showTotalItems } from "../../GlobalFunctions/GlobalFunctions";
import { UserContext } from "../../Context";

const statusTypes = {
  '1': constants.Dismissed,
  '2': constants.Probation,
  '3': constants.LeftProgram,
  '4': constants.Break,
  '5': constants.InActive,
  '6': constants.SlpMajor,
  '9': constants.Undeclared,
  '10': 'Graduated',
};

const FULL_TIME_YEARS = [...constants.StudentYearList]
  .filter((it) => it.type === 1 || it.type === 2)
  .map((it) => {
    return {
      id: it.id,
      label: it.title,
      prefix: it.prefix,
    }
  });

const PART_TIME_YEARS = [...constants.StudentYearList]
  .filter((it) => it.type === 2 || it.type === 3)
  .map((it) => {
    return {
      id: it.id,
      label: it.title,
      prefix: it.prefix,
    }
  });

const NewGPAHistory = () => {
  const { setSelectedMenuItem, setSelectedSubMenuItem, setBreadcrumbItems } = useContext(UserContext);
  const navigate = useNavigate();
  const [, , maxYear] = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [gpaLevels, setGpaLevels] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [tabKey, setTabKey] = useState(1);
  const [searchText, setSearchText] = useState('');

  const buildFullTimeColumns = (yearIndex, maxYear) => {
    let years = PART_TIME_YEARS.filter(item => item.id <= maxYear);
    if (yearIndex !== 6) {
      years = FULL_TIME_YEARS.slice(0, yearIndex);
    }
    const yearColumns = years.map((year, index) => {
      const semesterColumns = [
        {
          title: "Fall",
          align: "center",
          render: (_, record) => (
            <span className={isBelowAvg(record[`${year.prefix}_fall_gpa`], record?.year_in_program, 1) && 'text-red-500'}>{record?.[`${year.prefix}_fall_gpa`] || '-'}</span>
          )
        },
        {
          title: "Spring",
          align: "center",
          render: (_, record) => <span className={isBelowAvg(record[`${year.prefix}_spring_gpa`], record?.year_in_program, 2) && 'text-red-500'}>{record?.[`${year.prefix}_spring_gpa`] || '-'}</span>
        }
      ];
      semesterColumns.push({
        title: "Summer",
        align: "center",
        render: (_, record) =>
          <span className={isBelowAvg(record[`${year.prefix}_summer_gpa_one_gpa`], record?.year_in_program, 3) && 'text-red-500'}>{record?.[`${year.prefix}_summer_gpa_one_gpa`] || '-'}</span> ||
          <span className={isBelowAvg(record[`${year.prefix}_summer_gpa_two_gpa`], record?.year_in_program, 4) && 'text-red-500'}>{record?.[`${year.prefix}_summer_gpa_two_gpa`] || '-'}</span> || '-'
      });

      return {
        title: year.label,
        children: semesterColumns
      };
    });

    return [
      {
        title: "Student",
        dataIndex: "student",
        fixed: "left",
        width: '25%',
        render: (_, record) => {
          const status = record?.statusText || '';
          const condition = status === constants.Dismissed || status === constants.Probation
          return (
            <div
              className={`cursor-pointer font-bold underline ${condition ? 'text-red-600' : ''} w-full h-full`}
              onClick={() => redirectToStudentEdit(record)}
            >
              <span className={condition && 'text-red-500'}>
                <HighlightedText
                  text={record?.student || '-'}
                  highlight={searchText}
                />
              </span>
            </div>
          )
        }
      },
      {
        title: "Status",
        dataIndex: "status",
        fixed: "left",
        width: '10%',
        render: (_, record) => {
          const status = record?.statusText || '';
          const condition = status === constants.Dismissed || status === constants.Probation
          return <span className={condition && 'text-red-500'}>
            <HighlightedText
              text={status || '-'}
              highlight={searchText}
            />
          </span>
        }
      },
      ...yearColumns,
    ];
  };

  const getGpaLevels = () => {
    let student_type = tabKey !== 6 ? 1 : 2
    fetchStaffApi(`/get-gpa-levels?student_type=${student_type}`, 'GET').then((res) => {
      if (res?.code === '200') {
        setGpaLevels(res?.data || [])
      } else {
        setGpaLevels([])
      }
      fetchData()
    }).catch(() => { })
  }

  const fetchData = async () => {
    const modifyData = (data) => {
      return data?.map((it) => {
        return {
          ...it,
          statusText: statusTypes?.[it.status]
        }
      })
    }
    try {
      setLoading(true);
      setSearchText('');
      let payload = null;
      payload = { semester: 0 };
      payload.year = tabKey;

      fetchStaffApi(`/gpa-history`, 'POST', payload).then((res) => {
        setLoading(false);
        if (res?.code === '200' && res?.data?.length > 0) {
          setStudents(modifyData(res?.data));
          setFiltered(modifyData(res?.data));
        } else {
          setStudents([]);
          setFiltered([]);
        }
      })
    } catch (err) {
      console.log('err: ', err);
      setStudents([]);
      setFiltered([]);
    }
  };

  const isBelowAvg = (field, year, semester) => {
    if (!field) return false
    year = Number.parseInt(year);
    let cutoff = gpaLevels?.find((it) => Number.parseInt(it.semester_type) === semester && Number.parseInt(it.year_in_program) === year)?.gpa
    if (!cutoff) return false
    return Number.parseFloat(field) < Number.parseFloat(cutoff)
  }

  useEffect(() => {
    setStudents([]);
    setFiltered([]);
    setSearchText('');
    getGpaLevels();
  }, [tabKey]);

  useEffect(() => {
    setContextOnLoad('selectedMenuItem', setSelectedMenuItem, 'gpa-history')
    setContextOnLoad('selectedSubMenuItem', setSelectedSubMenuItem, 'gpa')
  }, [])

  const onSearch = (event) => {
    if (checkWhitespaces(event)) return
    const search = event.target.value;
    setSearchText(search);
    const searchFieldNames = [
      'student',
      'fifth_fall_gpa',
      'fifth_spring_gpa',
      'fifth_summer_gpa_one_gpa',
      'fifth_summer_gpa_two_gpa',
      'fourth_fall_gpa',
      'fourth_spring_gpa',
      'fourth_summer_gpa_one_gpa',
      'fourth_summer_gpa_two_gpa',
      'third_fall_gpa',
      'third_spring_gpa',
      'third_summer_gpa_one_gpa',
      'third_summer_gpa_two_gpa',
      'second_fall_gpa',
      'second_spring_gpa',
      'second_summer_gpa_one_gpa',
      'second_summer_gpa_two_gpa',
      'first_fall_gpa',
      'first_spring_gpa',
      'first_summer_gpa_one_gpa',
      'first_summer_gpa_two_gpa',
      'statusText',
      "part_time_gpa",
    ];
    let searchedValue = performSearch(search, students, searchFieldNames);
    setFiltered(searchedValue);
  };

  const redirectToStudentEdit = (record) => {
    breadcrumbHelper(breadcrumbRoutes, setBreadcrumbItems)
    const semesterData = JSON.parse(sessionStorage.getItem('semesterData') || []);
    navigate("/student-information", {
      state: {
        student_id: record?.id,
        student_type: record?.student_type,
        student_semester: record?.semester,
        from: constants.GpaHistory,
        from_route: "/gpa-history",
        tabkey: "student-details",
        tab_title: constants.StudentDetails,
        student_name: record?.student,
        isArchived: record?.is_active === '3',
        year_in_program: record?.year_in_program,
        faculty_id: record?.faculty_id,
        sem_id: semesterData?.find((item) => item?.semester_is_ended === "0")?.id
      }
    });
  }

  const breadcrumbRoutes = [{ title: constants.GpaHistory, path: '/gpa-history' }]

  const content = () => (
    <>
      <div className="flex justify-end mb-2">
        <Input
          onChange={onSearch}
          value={searchText}
          type="search"
          placeholder={constants.Search}
          className="w-full lg:w-44"
        />
      </div>

      <Table
        size="small"
        className="records-table data_table gpaHistory default-cursor"
        bordered
        rowKey="id"
        columns={buildFullTimeColumns(tabKey, maxYear)}
        dataSource={filtered}
        scroll={{ x: "max-content" }}
        tableLayout="fixed"
        pagination={{
          pageSize: 10,
          showTotal: (total, range) => showTotalItems(total, range, filtered),
          itemRender: getNextAndPreviousButtons,
        }}
      />
    </>
  );

  const items = [
    {
      key: 1,
      label: constants.Freshman,
      children: content(),
    },
    {
      key: 2,
      label: constants.Sophomore,
      children: content(),
    },
    {
      key: 3,
      label: constants.Junior,
      children: content(),
    },
    {
      key: 4,
      label: constants.Senior,
      children: content(),
    },
    {
      key: 5,
      label: constants.Graduate,
      children: content(),
    },
    {
      key: 6,
      label: constants.PartTime,
      children: content(),
    },
  ];

  return (
    <div>
      <Breadcrumbs routes={breadcrumbRoutes} />
      <Card className="container-border-custom">
        <Spin tip={constants.PleaseWait} spinning={loading}>
          <Tabs
            type="card"
            size='middle'
            onChange={(e) => setTabKey(Number.parseInt(e))}
            activeKey={tabKey}
            items={items}
          >
            <Input
              value={searchText}
              placeholder={constants.Search}
              onSearch={onSearch}
              className='w-full block lg:w-48 mb-2'
            />
          </Tabs>
        </Spin>
      </Card>
    </div>
  );
};

export default NewGPAHistory;